<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-screen overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">E-Ink Display Editor</h3>
        <div>
          <button
            @click="$emit('cancel')"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="p-4 border-b border-gray-200 bg-gray-50">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <!-- Color Tools -->
          <div class="flex items-center space-x-2">
            <span class="text-sm font-medium text-gray-700">Colors:</span>
            <button
              v-for="color in colors"
              :key="color.value"
              @click="selectedColor = color.value"
              :class=" [
                'w-8 h-8 rounded border-2 flex items-center justify-center text-xs font-bold transition-all',
                selectedColor === color.value 
                  ? 'border-blue-500 scale-110 shadow-md' 
                  : 'border-gray-300 hover:border-gray-400'
              ]"
              :style="{ backgroundColor: color.display }"
              :title="color.name"
            >
              {{ color.label }}
            </button>
          </div>

          <!-- Action Tools -->
          <div class="flex items-center space-x-2">
            <button
              @click="undo"
              :disabled="!canUndo"
              class="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              ↶ Undo
            </button>
            <button
              @click="redo"
              :disabled="!canRedo"
              class="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              ↷ Redo
            </button>
            <button
              @click="clearCanvas"
              class="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
              title="Clear All"
            >
              🗑 Clear
            </button>
            <!-- Upload Image Button -->
            <label class="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 cursor-pointer">
              <input type="file" accept="image/*" class="hidden" @change="onUploadImage" />
              Upload Image
            </label>
            <!-- Download Image Button -->
            <button
              @click="downloadImage"
              class="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              title="Download as PNG"
            >
              Download
            </button>
          </div>
        </div>
      </div>

      <!-- Canvas Area -->
      <div class="flex-1 overflow-auto p-4 bg-gray-100">
        <div class="flex items-center justify-center min-h-full">
          <div class="bg-white p-2 rounded shadow-lg">
            <!-- Canvas Info -->
            <div class="mb-2 text-center text-xs text-gray-500">
              90 × 128 pixels ({{ Math.round(zoom * 100) }}% zoom)
            </div>
            
            <!-- Canvas Container -->
            <div class="relative inline-block border border-gray-300 bg-black" style="touch-action:none; max-width:90vw; max-height:70vh; aspect-ratio:90/128;">
              <canvas
                ref="canvasRef"
                :width="displayWidth"
                :height="displayHeight"
                :style="{
                  width: '100%',
                  height: '100%',
                  cursor: isPanning ? 'grab' : 'crosshair',
                  background: '#222'
                }"
                class="block select-none"
                @mousedown="onPointerDown"
                @mousemove="onPointerMove"
                @mouseup="onPointerUp"
                @mouseleave="onPointerUp"
                @wheel.prevent="onWheel"
                @touchstart.prevent="onPointerDown"
                @touchmove.prevent="onPointerMove"
                @touchend.prevent="onPointerUp"
                @dragover.prevent="onCanvasDragOver"
                @drop.prevent="onCanvasDrop"
              />
            </div>
            <!-- Zoom Controls -->
            <div class="mt-2 flex justify-center space-x-2">
              <button
                @click="zoomOut"
                :disabled="zoom <= minZoom"
                class="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                -
              </button>
              <span class="px-2 py-1 text-xs text-gray-600">{{ Math.round(zoom * 100) }}%</span>
              <button
                @click="zoomIn"
                :disabled="zoom >= maxZoom"
                class="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
        <div class="text-sm text-gray-600">
          Data size: {{ getDataSizeString() }}
        </div>
        <div class="flex space-x-3">
          <button
            @click="$emit('cancel')"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="saveImage"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Save Image
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

// Props
const props = defineProps({
  initialImageData: {
    type: String,
    default: null
  }
})

// Emits
const emit = defineEmits(['save', 'cancel'])

// --- Offscreen buffer for pixel-perfect rendering (MUST be first!) ---
const CANVAS_WIDTH = 90
const CANVAS_HEIGHT = 128
const offscreenCanvas = document.createElement('canvas')
offscreenCanvas.width = CANVAS_WIDTH
offscreenCanvas.height = CANVAS_HEIGHT
const offscreenCtx = offscreenCanvas.getContext('2d')
// --- END Offscreen buffer ---

// Color definitions (2-bit encoding)
const colors = [
  { value: 0, name: 'White', label: 'W', display: '#FFFFFF' },    // 00
  { value: 1, name: 'Black', label: 'B', display: '#000000' },    // 01  
  { value: 2, name: 'Red', label: 'R', display: '#FF0000' }       // 10
]

// State
const canvasRef = ref(null)
const selectedColor = ref(1) // Default to black
const zoom = ref(1.0) // 1.0 = 100%
const minZoom = 1.0
const maxZoom = 8.0
const pan = ref({ x: 0, y: 0 })
const isDrawing = ref(false)
const isPanning = ref(false)
const lastPointer = ref({ x: 0, y: 0 })
const displayWidth = 540 // px, can be made responsive
const displayHeight = 768 // px, can be made responsive

// Image data (2D array of color values 0,1,2)
const imageData = ref(Array(CANVAS_HEIGHT).fill(null).map(() => Array(CANVAS_WIDTH).fill(0)))

// Command pattern for undo/redo
const commandHistory = ref([])
const currentCommandIndex = ref(-1)
const maxHistorySize = 50

// Computed
const canUndo = computed(() => currentCommandIndex.value >= 0)
const canRedo = computed(() => currentCommandIndex.value < commandHistory.value.length - 1)

// Command class
class DrawCommand {
  constructor(x, y, color, previousColor) {
    this.x = x
    this.y = y
    this.color = color
    this.previousColor = previousColor
  }

  execute(imageData) {
    imageData[this.y][this.x] = this.color
  }

  undo(imageData) {
    imageData[this.y][this.x] = this.previousColor
  }
}

// Batch command for continuous drawing
class BatchDrawCommand {
  constructor() {
    this.commands = []
  }

  addPixel(x, y, color, previousColor) {
    this.commands.push(new DrawCommand(x, y, color, previousColor))
  }

  execute(imageData) {
    this.commands.forEach(cmd => cmd.execute(imageData))
  }

  undo(imageData) {
    this.commands.forEach(cmd => cmd.undo(imageData))
  }
}

// Methods
function getPointerPos(e) {
  const rect = canvasRef.value.getBoundingClientRect()
  let clientX, clientY
  if (e.touches && e.touches.length) {
    clientX = e.touches[0].clientX
    clientY = e.touches[0].clientY
  } else {
    clientX = e.clientX
    clientY = e.clientY
  }
  return {
    x: clientX - rect.left,
    y: clientY - rect.top
  }
}

function getPixelCoordinates(e) {
  const { x, y } = getPointerPos(e)
  // Apply inverse pan/zoom
  const zx = (x - pan.value.x) / zoom.value
  const zy = (y - pan.value.y) / zoom.value
  return {
    x: Math.max(0, Math.min(CANVAS_WIDTH - 1, Math.floor(zx * CANVAS_WIDTH / displayWidth))),
    y: Math.max(0, Math.min(CANVAS_HEIGHT - 1, Math.floor(zy * CANVAS_HEIGHT / displayHeight)))
  }
}


let currentBatchCommand = null

function onPointerDown(e) {
  // Support panning with Alt, Ctrl, Meta, Space, or middle mouse
  const isPanKey = e.altKey || e.ctrlKey || e.metaKey || e.button === 1 || (e.type === 'mousedown' && e.buttons === 4)
  // Spacebar panning (keyboard)
  if (!isPanKey && e.type === 'mousedown' && window._spacePanning) {
    isPanning.value = true
    lastPointer.value = getPointerPos(e)
    return
  }
  if (isPanKey) {
    isPanning.value = true
    lastPointer.value = getPointerPos(e)
    return
  }
  isDrawing.value = true
  currentBatchCommand = new BatchDrawCommand()
  const { x, y } = getPixelCoordinates(e)
  setPixel(x, y)
}

function onPointerMove(e) {
  if (isPanning.value) {
    const pos = getPointerPos(e)
    const dx = pos.x - lastPointer.value.x
    const dy = pos.y - lastPointer.value.y
    pan.value.x += dx
    pan.value.y += dy
    clampPan()
    lastPointer.value = pos
    renderCanvas()
    return
  }
  if (!isDrawing.value) return
  const { x, y } = getPixelCoordinates(e)
  setPixel(x, y)
}

function onPointerUp(e) {
  if (isPanning.value) {
    isPanning.value = false
    return
  }
  if (isDrawing.value && currentBatchCommand && currentBatchCommand.commands.length > 0) {
    addCommand(currentBatchCommand)
  }
  isDrawing.value = false
  currentBatchCommand = null
}

function onWheel(e) {
  const oldZoom = zoom.value
  if (e.deltaY < 0) {
    zoom.value = Math.min(maxZoom, zoom.value * 1.1)
  } else {
    zoom.value = Math.max(minZoom, zoom.value / 1.1)
  }
  // Adjust pan so zoom is centered on pointer
  const rect = canvasRef.value.getBoundingClientRect()
  const px = e.clientX - rect.left
  const py = e.clientY - rect.top
  pan.value.x = (pan.value.x - px) * (zoom.value / oldZoom) + px
  pan.value.y = (pan.value.y - py) * (zoom.value / oldZoom) + py
  // Clamp pan
  clampPan()
  renderCanvas()
}

const setPixel = (x, y) => {
  if (x < 0 || x >= CANVAS_WIDTH || y < 0 || y >= CANVAS_HEIGHT) return
  if (imageData.value[y][x] === selectedColor.value) return // No change needed
  
  const previousColor = imageData.value[y][x]
  
  if (currentBatchCommand) {
    currentBatchCommand.addPixel(x, y, selectedColor.value, previousColor)
  }
  
  imageData.value[y][x] = selectedColor.value
  renderCanvas()
}

const addCommand = (command) => {
  // Remove any commands after current position
  commandHistory.value = commandHistory.value.slice(0, currentCommandIndex.value + 1)
  
  // Add new command
  commandHistory.value.push(command)
  currentCommandIndex.value++
  
  // Limit history size
  if (commandHistory.value.length > maxHistorySize) {
    commandHistory.value.shift()
    currentCommandIndex.value--
  }
}

const undo = () => {
  if (!canUndo.value) return
  
  const command = commandHistory.value[currentCommandIndex.value]
  command.undo(imageData.value)
  currentCommandIndex.value--
  renderCanvas()
}

const redo = () => {
  if (!canRedo.value) return
  
  currentCommandIndex.value++
  const command = commandHistory.value[currentCommandIndex.value]
  command.execute(imageData.value)
  renderCanvas()
}

const clearCanvas = () => {
  const clearCommand = new BatchDrawCommand()
  
  for (let y = 0; y < CANVAS_HEIGHT; y++) {
    for (let x = 0; x < CANVAS_WIDTH; x++) {
      if (imageData.value[y][x] !== 0) {
        clearCommand.addPixel(x, y, 0, imageData.value[y][x])
        imageData.value[y][x] = 0
      }
    }
  }
  
  if (clearCommand.commands.length > 0) {
    addCommand(clearCommand)
  }
  
  renderCanvas()
}

const zoomIn = () => {
  zoom.value = Math.min(maxZoom, zoom.value * 1.25)
  clampPan()
  renderCanvas()
}
const zoomOut = () => {
  zoom.value = Math.max(minZoom, zoom.value / 1.25)
  clampPan()
  renderCanvas()
}
function clampPan() {
  // Clamp pan so the image stays in view
  const maxPanX = 0
  const minPanX = displayWidth - displayWidth * zoom.value
  const maxPanY = 0
  const minPanY = displayHeight - displayHeight * zoom.value
  pan.value.x = Math.min(maxPanX, Math.max(minPanX, pan.value.x))
  pan.value.y = Math.min(maxPanY, Math.max(minPanY, pan.value.y))
}
// --- END ZOOM/PAN LOGIC ---

// --- Upload/Download Image + Drag & Drop + Clipboard Paste ---
function applyImageToCanvas(img) {
  // Save current image for undo
  const prev = imageData.value.map(row => [...row])
  // Draw image to temp canvas, scale to cover
  const tempCanvas = document.createElement('canvas')
  tempCanvas.width = CANVAS_WIDTH
  tempCanvas.height = CANVAS_HEIGHT
  const tempCtx = tempCanvas.getContext('2d')
  // Calculate cover scaling
  const scale = Math.max(CANVAS_WIDTH / img.width, CANVAS_HEIGHT / img.height)
  const sw = CANVAS_WIDTH / scale
  const sh = CANVAS_HEIGHT / scale
  const sx = (img.width - sw) / 2
  const sy = (img.height - sh) / 2
  tempCtx.drawImage(img, sx, sy, sw, sh, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)
  // Read pixels and map to palette
  const imgData = tempCtx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT).data
  // We'll build the new image as a 2D array
  const uploaded = Array(CANVAS_HEIGHT)
  for (let y = 0; y < CANVAS_HEIGHT; y++) {
    uploaded[y] = Array(CANVAS_WIDTH)
    for (let x = 0; x < CANVAS_WIDTH; x++) {
      const idx = (y * CANVAS_WIDTH + x) * 4
      const r = imgData[idx]
      const g = imgData[idx + 1]
      const b = imgData[idx + 2]
      // Find nearest color (Euclidean distance)
      let minDist = Infinity, best = 0
      for (let c = 0; c < colors.length; c++) {
        const col = colors[c]
        const cr = parseInt(col.display.slice(1, 3), 16)
        const cg = parseInt(col.display.slice(3, 5), 16)
        const cb = parseInt(col.display.slice(5, 7), 16)
        const dist = (r - cr) ** 2 + (g - cg) ** 2 + (b - cb) ** 2
        if (dist < minDist) {
          minDist = dist
          best = col.value
        }
      }
      uploaded[y][x] = best
      imageData.value[y][x] = best
    }
  }
  // Add to undo stack, storing a deep copy of uploaded and prev
  addCommand({
    execute: (imgData) => {
      for (let y = 0; y < CANVAS_HEIGHT; y++)
        for (let x = 0; x < CANVAS_WIDTH; x++)
          imgData[y][x] = uploaded[y][x]
    },
    undo: (imgData) => {
      for (let y = 0; y < CANVAS_HEIGHT; y++)
        for (let x = 0; x < CANVAS_WIDTH; x++)
          imgData[y][x] = prev[y][x]
    }
  })
  renderCanvas()
}

function onUploadImage(e) {
  const file = e.target.files[0]
  if (!file) return
  const img = new window.Image()
  img.onload = () => {
    applyImageToCanvas(img)
    e.target.value = '' // reset file input
  }
  img.src = URL.createObjectURL(file)
}

function onCanvasDrop(e) {
  e.preventDefault()
  if (!e.dataTransfer.files || !e.dataTransfer.files.length) return
  const file = e.dataTransfer.files[0]
  if (!file.type.startsWith('image/')) return
  const img = new window.Image()
  img.onload = () => applyImageToCanvas(img)
  img.src = URL.createObjectURL(file)
}

function onCanvasDragOver(e) {
  e.preventDefault()
}

function downloadImage() {
  // Render current imageData to a temp canvas and download as PNG
  const tempCanvas = document.createElement('canvas')
  tempCanvas.width = CANVAS_WIDTH
  tempCanvas.height = CANVAS_HEIGHT
  const tempCtx = tempCanvas.getContext('2d')
  for (let y = 0; y < CANVAS_HEIGHT; y++) {
    for (let x = 0; x < CANVAS_WIDTH; x++) {
      const colorValue = imageData.value[y][x]
      const color = colors.find(c => c.value === colorValue)
      tempCtx.fillStyle = color ? color.display : '#FFF'
      tempCtx.fillRect(x, y, 1, 1)
    }
  }
  const link = document.createElement('a')
  link.download = 'eink-image.png'
  link.href = tempCanvas.toDataURL('image/png')
  link.click()
}
// --- Clipboard Paste Support ---
function onPaste(e) {
  if (!e.clipboardData) return
  const items = e.clipboardData.items
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) {
        const img = new window.Image()
        img.onload = () => applyImageToCanvas(img)
        img.src = URL.createObjectURL(file)
        e.preventDefault()
        break
      }
    }
  }
}
// --- END Upload/Download Image + Drag & Drop ---

const renderCanvas = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  // Draw to offscreen buffer
  offscreenCtx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)
  for (let y = 0; y < CANVAS_HEIGHT; y++) {
    for (let x = 0; x < CANVAS_WIDTH; x++) {
      const colorValue = imageData.value[y][x]
      const color = colors.find(c => c.value === colorValue)
      if (color) {
        offscreenCtx.fillStyle = color.display
        offscreenCtx.fillRect(x, y, 1, 1)
      }
    }
  }
  // Draw offscreen buffer to display canvas
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.save()
  ctx.imageSmoothingEnabled = false
  ctx.translate(pan.value.x, pan.value.y)
  ctx.scale(zoom.value, zoom.value)
  ctx.drawImage(offscreenCanvas, 0, 0, displayWidth, displayHeight)
  // Draw grid if zoom > 3.0
  if (zoom.value > 3.0) {
    ctx.save()
    ctx.lineWidth = 1 / zoom.value
    ctx.strokeStyle = 'rgba(0,0,0,0.25)'
    // Vertical lines
    for (let x = 1; x < CANVAS_WIDTH; x++) {
      const px = x * (displayWidth / CANVAS_WIDTH)
      ctx.beginPath()
      ctx.moveTo(px + 0.5 / zoom.value, 0)
      ctx.lineTo(px + 0.5 / zoom.value, displayHeight)
      ctx.stroke()
    }
    // Horizontal lines
    for (let y = 1; y < CANVAS_HEIGHT; y++) {
      const py = y * (displayHeight / CANVAS_HEIGHT)
      ctx.beginPath()
      ctx.moveTo(0, py + 0.5 / zoom.value)
      ctx.lineTo(displayWidth, py + 0.5 / zoom.value)
      ctx.stroke()
    }
    ctx.restore()
  }
  ctx.restore()
}

// Data encoding/decoding (2 bits per pixel)
const encodeImageData = () => {
  const totalPixels = CANVAS_WIDTH * CANVAS_HEIGHT
  const bytesNeeded = Math.ceil(totalPixels / 4) // 4 pixels per byte (2 bits each)
  const bytes = new Uint8Array(bytesNeeded)
  
  let byteIndex = 0
  let bitOffset = 0
  
  for (let y = 0; y < CANVAS_HEIGHT; y++) {
    for (let x = 0; x < CANVAS_WIDTH; x++) {
      const pixelValue = imageData.value[y][x]
      
      // Pack 2 bits into current byte
      bytes[byteIndex] |= (pixelValue << (6 - bitOffset))
      
      bitOffset += 2
      if (bitOffset >= 8) {
        bitOffset = 0
        byteIndex++
      }
    }
  }
  
  // Convert to base64 for storage
  return btoa(String.fromCharCode(...bytes))
}

const decodeImageData = (base64Data) => {
  if (!base64Data) return
  
  try {
    const binaryString = atob(base64Data)
    const bytes = new Uint8Array(binaryString.length)
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    
    let byteIndex = 0
    let bitOffset = 0
    
    for (let y = 0; y < CANVAS_HEIGHT; y++) {
      for (let x = 0; x < CANVAS_WIDTH; x++) {
        // Extract 2 bits from current byte
        const pixelValue = (bytes[byteIndex] >> (6 - bitOffset)) & 0x03
        imageData.value[y][x] = pixelValue
        
        bitOffset += 2
        if (bitOffset >= 8) {
          bitOffset = 0
          byteIndex++
        }
      }
    }
    
    renderCanvas()
  } catch (error) {
    console.error('Failed to decode image data:', error)
  }
}

const getDataSizeString = () => {
  const encoded = encodeImageData()
  const bytes = Math.ceil((CANVAS_WIDTH * CANVAS_HEIGHT * 2) / 8)
  return `${encoded.length} chars (${bytes} bytes)`
}

const saveImage = () => {
  const encodedData = encodeImageData()
  emit('save', encodedData)
}

// Keyboard shortcuts
const handleKeydown = (event) => {
  if (event.ctrlKey || event.metaKey) {
    switch (event.key.toLowerCase()) {
      case 'z':
        event.preventDefault()
        if (event.shiftKey) {
          redo()
        } else {
          undo()
        }
        break
      case 'y':
        event.preventDefault()
        redo()
        break
    }
  }
  
  // Color shortcuts
  switch (event.key) {
    case '1':
      selectedColor.value = 0 // White
      break
    case '2':
      selectedColor.value = 1 // Black
      break
    case '3':
      selectedColor.value = 2 // Red
      break
  }
}

// Lifecycle
onMounted(() => {
  // Center image initially
  zoom.value = 1.0
  pan.value.x = 0
  pan.value.y = 0
  nextTick(() => {
    // Load initial image data if provided
    if (props.initialImageData) {
      decodeImageData(props.initialImageData)
    } else {
      renderCanvas() // Render empty canvas
    }
  })
  // Spacebar panning support
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Space') window._spacePanning = true
  })
  window.addEventListener('keyup', (e) => {
    if (e.code === 'Space') window._spacePanning = false
  })
  document.addEventListener('keydown', handleKeydown)
  // Clipboard paste support
  document.addEventListener('paste', onPaste)
})
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('keydown', () => {})
  window.removeEventListener('keyup', () => {})
  document.removeEventListener('paste', onPaste)
})
</script>